// @flow
import React from 'react';
import i18n from 'app/common/lib/i18n';
import { Experiment } from 'app/common/react/components/experiments';
import { type NuxStep } from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, IconButton, Text } from 'gestalt';

export type TableType = 'dots' | 'single' | 'all';

type ContentColors = 'darkGray' | 'red';

type Props = {|
  currentNuxStep: NuxStep,
  onPrevStepClick: () => void,
  steps: Array<NuxStep>,
  renderType: TableType,
|};

const BACK_BUTTON_WIDTH = 40;

const SMALL_CIRCLE_SIZE = '10px';
const LARGE_CIRCLE_SIZE = '15px';

// Different distance between dots, depending on if they need more space because of text
const DOTS_DISTANCE = '30px';
const SINGLE_LABEL_DOTS_DISTANCE = '45px';
const LABELED_DOTS_DISTANCE = '85px';

const SMALL_CIRCLE_TOP_MARGIN = '3px';
const LARGE_CIRCLE_TOP_MARGIN = '0px';
const TEXT_MARGIN_SMALL_CIRCLE = '6px';
const TEXT_MARGIN_LARGE_CIRCLE = '4px';

export const circleLabel = (
  color: ContentColors,
  distanceBetween: string,
  size: string,
  text: string,
  unwrapText: boolean,
) => (
  <Box
    alignItems="center"
    direction="column"
    display="flex"
    justifyContent="center"
    margin={2}
    width={distanceBetween}
  >
    <Box
      dangerouslySetInlineStyle={
        // can't use marginTop because values are not multiples of boints
        // necessary to align large and small cirlce centers
        {
          __style: {
            marginTop:
              size === SMALL_CIRCLE_SIZE ? SMALL_CIRCLE_TOP_MARGIN : LARGE_CIRCLE_TOP_MARGIN,
          },
        }
      }
      data-test-id="nux-toc-dot"
      color={color}
      height={size}
      shape="circle"
      width={size}
    />
    {text && (
      <Box
        dangerouslySetInlineStyle={
          // can't use marginTop because values are not mutliples of boints
          {
            __style: {
              whiteSpace: unwrapText ? 'nowrap' : '',
              marginTop:
                size === SMALL_CIRCLE_SIZE ? TEXT_MARGIN_SMALL_CIRCLE : TEXT_MARGIN_LARGE_CIRCLE,
            },
          }
        }
        data-test-id="nux-toc-text"
        display="flex"
        wrap
      >
        <Text align="center" bold color={color} size="sm">
          {text}
        </Text>
      </Box>
    )}
  </Box>
);

// Copied from webapp/app/common/react/components/closeup/Buttons/BackButton.js
// eslint-disable max-len
const ARROW_LEFT =
  'M24 12a2 2 0 0 1-2 2H7.676l4.631 4.586a2 2 0 1 1-2.829 2.828L0 12l9.478-9.414a2 2 0 0 1 2.829 2.828L7.676 10H22a2 2 0 0 1 2 2';
// eslint-enable max-len

function NuxTableOfContents({ renderType, currentNuxStep, steps, onPrevStepClick }: Props) {
  const stepNames = steps.map((step) => step.moduleName);
  const currentStepIndex = stepNames.indexOf(currentNuxStep.moduleName);

  function renderOneContent(contentToRender: { moduleName: string }) {
    // calculate how to render complete / not complete
    const contentIsFutureStep = stepNames.indexOf(contentToRender.moduleName) > currentStepIndex;
    const color = contentIsFutureStep ? 'darkGray' : 'red';
    const size = contentIsFutureStep ? SMALL_CIRCLE_SIZE : LARGE_CIRCLE_SIZE;

    // calculate renderType-dependent variables (what text to render, distance between dots)
    let text = '';
    let distanceBetween = DOTS_DISTANCE;
    let unwrapText = false;
    if (renderType === 'single') {
      distanceBetween = SINGLE_LABEL_DOTS_DISTANCE;
      unwrapText = true;
      const contentIsCurrentStep = contentToRender.moduleName === currentNuxStep.moduleName;
      const nameMap = {
        NuxGenderStep: i18n._('You', 'Label for NUX gender step in a table of contents'),
        NuxLocaleCountryStep: i18n._(
          'Your Language',
          'Label for NUX language/locale step in a table of contents',
        ),
        NuxPicker: i18n._('Your Interests', 'Label for NUX topic picker in a table of contents'),
        NuxExtensionUpsell: i18n._(
          'Your Browser',
          'Label for NUX Browser Extension in a table of contents',
        ),
      };
      text = contentIsCurrentStep ? nameMap[contentToRender.moduleName] : '';
    }
    if (renderType === 'all') {
      distanceBetween = LABELED_DOTS_DISTANCE;
      const nameMap = {
        NuxGenderStep: i18n._(
          'You',
          'Label for NUX gender step in a table of contents, where text should be short since there is not much space to show it',
        ),
        NuxLocaleCountryStep: i18n._(
          'Language',
          'Label for NUX language/locale step in a table of contents, where text should be short since there is not much space to show it',
        ),
        NuxPicker: i18n._(
          'Interests',
          'Label for NUX topic picker in a table of contents, where text should be short since there is not much space to show it',
        ),
        NuxExtensionUpsell: i18n._(
          'Extension',
          'Label for NUX Browser Extension in a table of contents, where text should be short since there is not much space to show it',
        ),
      };
      text = nameMap[contentToRender.moduleName] || '';
    }
    return circleLabel(color, distanceBetween, size, text, unwrapText);
  }

  const backButton = currentStepIndex > 0 && (
    <Experiment name="web_nux_back_button">
      {({ anyEnabled }) =>
        anyEnabled && (
          <Box display="flex" alignItems="center">
            <IconButton
              accessibilityLabel={i18n._(
                'Go back to previous step',
                'Accessibility label on NUX back button',
              )}
              dangerouslySetSvgPath={{
                __path: ARROW_LEFT,
              }}
              iconColor="gray"
              size="sm"
              onClick={onPrevStepClick}
            />
          </Box>
        )
      }
    </Experiment>
  );

  if (steps.length > 1) {
    return (
      <Box data-test-id="nux-toc-container" display="flex" justifyContent="between" padding={4}>
        <Box width={BACK_BUTTON_WIDTH}>{backButton}</Box>
        <Box display="flex" justifyContent="center">
          {steps.map((step, index) => (
            <div key={index}>{renderOneContent(step)}</div>
          ))}
        </Box>
        <Box
          width={
            BACK_BUTTON_WIDTH /* This is used for balancing the width of the back button and keeping symmetry */
          }
        />
      </Box>
    );
  }
}

// $FlowIssue Currently installed version of Flow doesn't understand React.memo
export default React.memo(NuxTableOfContents);
